import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import { Check, Pencil, Pin, PinOff, StickyNote, X as XIcon } from "lucide-react";
import { color, radius, space, typography } from "@tokens/index";
import { Badge, Button, Text } from "../../primitives";
import type { WorkItem } from "../../../experience-shell/workflows";
import { formatRelativeTime } from "./formatters";
import { ActionButton, EmptyState } from "./shared";
import { PanelHeader } from "./PanelHeader";
import { ThreadSection } from "./ThreadSection";
import { hairline } from "./expandMotion";
import { InsertedRow } from "./InsertedRow";
import { useNewRowDetector } from "./useNewRowDetector";
import { NoteComposerRow, NoteComposerTrigger } from "./NoteComposer";
import { useNoteComposer } from "./useNoteComposer";
import styles from "./Notes.module.css";
import type { PanelNavigation, ThreadSectionProps } from "./types";

// ---------------------------------------------------------------------------
// NotesListPanel: the "Notes" lane of note-first workflow kinds.
// ---------------------------------------------------------------------------

export function NotesListPanel({
  notes,
  onSelectNote,
  onAddNote,
  onBackToRail,
}: {
  readonly notes: readonly WorkItem[];
  readonly onSelectNote: (noteId: string) => void;
  readonly onAddNote: (body: string) => void;
} & PanelNavigation): ReactElement {
  const composer = useNoteComposer(onAddNote);

  // Root notes only, pinned first. Task-attached notes live under their task.
  const ordered = useMemo<WorkItem[]>(() => {
    const root = notes.filter((n) => n.parentWorkItemId === null);
    return [...root.filter((n) => n.pinned), ...root.filter((n) => !n.pinned)];
  }, [notes]);
  const isNewRow = useNewRowDetector(ordered.map((n) => n.id));

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
      <PanelHeader
        onBackToRail={onBackToRail}
        actionsInline
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: space[2],
              minWidth: 0,
              minHeight: 32,
            }}
          >
            <StickyNote size={16} strokeWidth={2} color={color.note.accent} aria-hidden="true" />
            <Text as="h2" role="title" size="md" weight="semibold" truncate style={{ margin: 0 }}>
              Notes
            </Text>
            <Badge variant="neutral" size="sm">
              {String(ordered.length)}
            </Badge>
          </div>
        }
        actions={<NoteComposerTrigger composer={composer} size="md" variant="secondary" />}
      />

      <NoteComposerRow composer={composer} placeholder="Capture a note…" />

      {ordered.length === 0 ? (
        <EmptyState
          icon={<StickyNote size={24} strokeWidth={1.5} aria-hidden="true" />}
          title="No notes yet — add one to capture context."
        />
      ) : (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            padding: `0 ${space[4]} ${space[4]}`,
          }}
        >
          {ordered.map((note) => (
            <InsertedRow key={note.id} isNew={isNewRow(note.id)} gap={8}>
              <NoteListRow note={note} onSelect={() => onSelectNote(note.id)} />
            </InsertedRow>
          ))}
        </div>
      )}
    </div>
  );
}

function NoteListRow({
  note,
  onSelect,
}: {
  readonly note: WorkItem;
  readonly onSelect: () => void;
}): ReactElement {
  const author = note.createdByDisplayName ?? note.createdBy ?? "Unknown";
  return (
    <button
      type="button"
      onClick={onSelect}
      className={styles.noteRow}
      style={{
        appearance: "none",
        width: "100%",
        minWidth: 0,
        textAlign: "left",
        display: "flex",
        alignItems: "flex-start",
        gap: space[3],
        padding: `${space[2]} ${space[3]} ${space[2]} ${space[4]}`,
        position: "relative",
        borderRadius: radius.md,
        cursor: "pointer",
        border: "none",
        color: color.fg.default,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          top: space[2],
          bottom: space[2],
          width: 2,
          borderRadius: 1,
          background: color.note.accent,
        }}
      />
      <span
        style={{ display: "inline-flex", flexShrink: 0, color: color.note.accent, paddingTop: 2 }}
      >
        {note.pinned ? (
          <Pin size={15} strokeWidth={2.25} aria-hidden="true" />
        ) : (
          <StickyNote size={15} strokeWidth={2.25} aria-hidden="true" />
        )}
      </span>
      <span style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1, gap: 2 }}>
        <Text role="body" size="sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {note.body}
        </Text>
        <Text role="caption" size="xs" tone="subtle" truncate>
          {note.pinned ? "Pinned · " : ""}
          {author} · {formatRelativeTime(note.createdAt)}
        </Text>
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// NotePanel: one note plus its child threads.
// ---------------------------------------------------------------------------

export function NotePanel({
  note,
  isPinning,
  onPinNote,
  isEditingNote,
  onEditNote,
  onBackToRail,
  actionsInline,
  threadSection,
}: {
  readonly note: WorkItem;
  readonly isPinning: boolean;
  readonly onPinNote: (noteId: string, pinned: boolean) => void;
  readonly isEditingNote: boolean;
  readonly onEditNote: ((noteId: string, body: string) => void) | undefined;
  readonly actionsInline: boolean;
  readonly threadSection: ThreadSectionProps;
} & PanelNavigation): ReactElement {
  const author = note.createdByDisplayName ?? note.createdBy ?? "Unknown";
  const canEdit = onEditNote !== undefined;

  // The draft re-syncs when a different note is selected or the persisted
  // body changes. Save turns editing off before the cache patch lands.
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState(note.body);
  useEffect(() => {
    setIsEditing(false);
    setEditDraft(note.body);
  }, [note.id, note.body]);

  const handleStartEdit = useCallback(() => {
    setEditDraft(note.body);
    setIsEditing(true);
  }, [note.body]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditDraft(note.body);
  }, [note.body]);

  const handleSaveEdit = useCallback(() => {
    if (onEditNote === undefined) return;
    const body = editDraft.trim();
    // A blank or unchanged body closes the editor without a request.
    if (body.length === 0 || body === note.body) {
      setIsEditing(false);
      return;
    }
    onEditNote(note.id, body);
    setIsEditing(false);
  }, [onEditNote, editDraft, note.id, note.body]);

  const isDirty = editDraft.trim().length > 0 && editDraft.trim() !== note.body;

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
      <PanelHeader
        onBackToRail={onBackToRail}
        actionsInline={actionsInline}
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: space[2],
              minWidth: 0,
              minHeight: 32,
            }}
          >
            <StickyNote size={16} strokeWidth={2} color={color.note.accent} aria-hidden="true" />
            <Text as="h2" role="title" size="md" weight="semibold" truncate style={{ margin: 0 }}>
              Note
            </Text>
            {note.pinned && (
              <Badge variant="warning" size="sm">
                Pinned
              </Badge>
            )}
            <Text role="caption" size="xs" tone="subtle" truncate style={{ marginLeft: space[1] }}>
              {author} · {formatRelativeTime(note.createdAt)}
            </Text>
          </div>
        }
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: space[2], flexWrap: "wrap" }}>
            <ActionButton
              label={note.pinned ? "Unpin" : "Pin"}
              icon={
                note.pinned ? (
                  <PinOff size={14} strokeWidth={2.5} aria-hidden="true" />
                ) : (
                  <Pin size={14} strokeWidth={2.5} aria-hidden="true" />
                )
              }
              tone="secondary"
              disabled={isPinning}
              busy={isPinning}
              onClick={() => onPinNote(note.id, !note.pinned)}
            />
            {canEdit && (
              <ActionButton
                label={isEditing ? "Cancel" : "Edit"}
                icon={
                  isEditing ? (
                    <XIcon size={14} strokeWidth={2.5} aria-hidden="true" />
                  ) : (
                    <Pencil size={14} strokeWidth={2.5} aria-hidden="true" />
                  )
                }
                tone="ghost"
                disabled={isEditingNote}
                onClick={isEditing ? handleCancelEdit : handleStartEdit}
              />
            )}
          </div>
        }
      >
        {isEditing ? (
          <div style={{ display: "flex", flexDirection: "column", gap: space[2] }}>
            <textarea
              autoFocus
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveEdit();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  handleCancelEdit();
                }
              }}
              aria-label="Edit note body"
              disabled={isEditingNote}
              style={{
                width: "100%",
                minHeight: 96,
                maxHeight: "40dvh",
                resize: "vertical",
                padding: space[3],
                borderRadius: radius.md,
                background: color.bg.surface,
                border: hairline,
                color: color.fg.default,
                fontFamily: typography.family.system,
                fontSize: typography.scale.sm,
                lineHeight: 1.5,
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: space[2] }}>
              <Button
                variant="primary"
                size="sm"
                disabled={!isDirty || isEditingNote}
                onClick={handleSaveEdit}
              >
                <Check size={14} strokeWidth={2.5} aria-hidden="true" />
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div
            style={{
              position: "relative",
              padding: `${space[2]} ${space[3]} ${space[2]} ${space[4]}`,
              borderRadius: radius.md,
              background: color.bg.surface2,
              maxHeight: "40dvh",
              overflowY: "auto",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 0,
                top: space[2],
                bottom: space[2],
                width: 2,
                borderRadius: 1,
                background: color.note.accent,
              }}
            />
            <Text role="body" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {note.body}
            </Text>
          </div>
        )}
      </PanelHeader>

      <ThreadSection {...threadSection} />
    </div>
  );
}
